import {
	ActionIcon,
	Button,
	Card,
	Group,
	Skeleton,
	Stack,
	Tabs,
	Text,
	Title,
	Tooltip,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { useSnapshot } from "valtio";
import { assistantLogTexts } from "@/locales/assistant-log";
import { i18nStore } from "@/store/i18n";
import { useAiAssistantAdminContext } from "./ai-assistant-admin-context";
import { LogFilters } from "./log-filters";
import { LogStats } from "./log-stats";
import { LogUsersTable } from "./log-users-table";
import { UsageLogCard } from "./usage-log-card";
import { useAssistantLog } from "./use-assistant-log";
import { useAssistantUsageLog } from "./use-assistant-usage-log";

/**
 * Lapis 1 sub menu "Log Percakapan" (`/admin/ai-assistant/log`): agregat per
 * pengguna + tab "Semua Giliran" yang memuat `UsageLogCard` — kartu yang
 * sebelumnya menumpang di halaman Pemakaian & Biaya (rancangan 08 §1–§3).
 */
export function LogPage() {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const t = assistantLogTexts[lang];
	const { overview } = useAiAssistantAdminContext();
	const log = useAssistantLog(overview.settings);
	const usageLog = useAssistantUsageLog(overview.settings);

	const page = log.page;
	const shownFrom = page && page.total > 0 ? log.offset + 1 : 0;
	const shownTo = page ? log.offset + page.rows.length : 0;

	return (
		<Stack gap="lg">
			<Group justify="space-between" align="flex-start">
				<Stack gap={2}>
					<Title order={3}>{t.title}</Title>
					<Text size="sm" c="dimmed">
						{t.subtitle}
					</Text>
				</Stack>
				<Tooltip label={t.reload}>
					<ActionIcon
						variant="light"
						onClick={log.reload}
						loading={log.loading}
						aria-label={t.reload}
					>
						<IconRefresh size={18} />
					</ActionIcon>
				</Tooltip>
			</Group>

			<Tabs defaultValue="users" keepMounted={false}>
				<Tabs.List>
					<Tabs.Tab value="users">{t.tabUsers}</Tabs.Tab>
					<Tabs.Tab value="turns">{t.tabTurns}</Tabs.Tab>
				</Tabs.List>

				<Tabs.Panel value="users" pt="md">
					<Stack gap="md">
						<LogFilters
							filters={log.filters}
							setFilters={log.setFilters}
							search={log.search}
							setSearch={log.setSearch}
						/>
						<LogStats overview={page?.overview ?? null} />

						{log.error && (
							<Text size="sm" c="red" data-log-error>
								{log.error}
							</Text>
						)}

						{log.loading ? (
							<Stack gap="xs" data-log-loading>
								<Text size="sm" c="dimmed">
									{t.loading}
								</Text>
								<Skeleton height={220} radius="sm" />
							</Stack>
						) : (
							page && (
								<Card withBorder radius="md" p={0}>
									{page.rows.length === 0 ? (
										<Text size="sm" c="dimmed" p="lg" data-log-empty>
											{t.empty}
										</Text>
									) : (
										<LogUsersTable rows={page.rows} />
									)}
									<Group justify="space-between" p="md" wrap="wrap">
										<Text size="xs" c="dimmed">
											{t.footerRange(
												String(shownFrom),
												String(shownTo),
												String(page.total),
											)}
										</Text>
										<Group gap="xs">
											<Button
												variant="default"
												size="xs"
												disabled={log.offset === 0}
												onClick={() =>
													log.goToOffset(Math.max(0, log.offset - log.pageSize))
												}
											>
												{t.prevPage}
											</Button>
											<Button
												variant="default"
												size="xs"
												disabled={shownTo >= page.total}
												onClick={() =>
													log.goToOffset(log.offset + log.pageSize)
												}
											>
												{t.nextPage}
											</Button>
										</Group>
									</Group>
								</Card>
							)
						)}
					</Stack>
				</Tabs.Panel>

				<Tabs.Panel value="turns" pt="md">
					<UsageLogCard log={usageLog} />
				</Tabs.Panel>
			</Tabs>
		</Stack>
	);
}
